import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileCheck2, Loader2, UploadCloud, X } from "lucide-react";

const MAX_BYTES = 5 * 1024 * 1024;
const MAX_SIDE = 1600;

// Re-encode any image the browser can decode as a compact JPEG so phone photos upload reliably.
const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("encode"));
        const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
        resolve(new File([blob], name, { type: "image/jpeg" }));
      }, "image/jpeg", 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
    img.src = url;
  });

export const FileDrop = ({ label, accept, file, onChange, hint, testId }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (f) => {
    if (!f) return;
    if (f.type === "application/pdf") {
      if (f.size > MAX_BYTES) return toast.error("PDF must be smaller than 5 MB.");
      return onChange(f);
    }
    if (!f.type.startsWith("image/") && !/\.(heic|heif)$/i.test(f.name)) return toast.error("Please choose a photo or a PDF file.");
    setBusy(true);
    try {
      onChange(await compressImage(f));
    } catch (e) {
      toast.error("This image format isn't supported. Please choose a JPG or PNG photo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <p className="text-[13px] font-semibold text-navy">{label} <span className="text-red-600">*</span></p>
      <div
        onClick={() => ref.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
        className={`flex cursor-pointer items-center gap-4 rounded-xl border-2 border-dashed p-5 transition-colors ${file ? "border-leaf bg-leaf-light/40" : "border-slate-300 hover:border-royal hover:bg-royal-light/30"}`}
        data-testid={`${testId}-dropzone`}
      >
        {busy ? <Loader2 className="h-10 w-10 animate-spin text-royal" />
          : file && file.type.startsWith("image/") ? <img src={URL.createObjectURL(file)} alt="preview" className="h-14 w-14 rounded-lg object-cover" />
          : file ? <FileCheck2 className="h-10 w-10 text-leaf" /> : <UploadCloud className="h-10 w-10 text-slate-400" />}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-navy">{busy ? "Preparing image…" : file ? file.name : "Click or drop file to upload"}</p>
          <p className="text-xs text-slate-500">{hint}</p>
        </div>
        {file && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onChange(null); }} className="rounded-md p-1 text-slate-500 hover:bg-white" aria-label="Remove file" data-testid={`${testId}-remove`}>
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testId}-input`} />
    </div>
  );
};
